import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, LoadingOverlay } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateCommunityFeedItemFile } from 'src/Hooks/Mutations/useCreateCommunityFeedItemFile';

import styles from './MemoryImageEdit.module.scss';
import { Props } from './MemoryImageEdit.props';

/**
 * This is an photo shown in the list of photos during the edit process of memories
 */
const MemoryImageEdit = ({ photo, isCover, onDelete, onUploaded }: Props) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.id,
  });

  let url = '';
  let isNativeFile = false;

  if (photo.serverFile) url = photo.serverFile.previewLargeUrl ?? photo.serverFile.url;
  else if (photo.nativeFile) {
    url = URL.createObjectURL(photo.nativeFile);
    isNativeFile = true;
  }

  const isUploading = useRef(false);
  const { mutateAsync, isLoading } = useCreateCommunityFeedItemFile();
  useEffect(() => {
    const shouldUpload = photo.nativeFile && !photo.serverFile && !isUploading.current;
    if (!shouldUpload) return;

    isUploading.current = true;

    async function upload() {
      const uploadedFile = await mutateAsync(photo.nativeFile!);
      onUploaded(uploadedFile);
    }

    upload();
  }, []);

  return (
    <div
      ref={setNodeRef}
      className={styles.photo}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 0,
      }}
    >
      <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ blur: 2, radius: 8 }} />

      <img
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        src={url}
        onLoad={() => isNativeFile && URL.revokeObjectURL(url)}
      />

      {/* La couverture n'est pas un choix caché : c'est la première photo, et on le dit. */}
      {isCover && !isDragging ? (
        <span className={styles.coverBadge}>{t('community.memory.cover', 'Couverture')}</span>
      ) : null}

      {!isDragging ? (
        <ActionIcon radius="xl" color="#0e5b61" className={styles.removeImage} onClick={onDelete}>
          <IconX style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      ) : null}
    </div>
  );
};

export default MemoryImageEdit;
